import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronsUpDown, Plus, User } from 'lucide-react'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import NewTeamModal from './NewTeamModal'
import { useState } from 'react'
import ProtectedContent, {
  hasAccessToContent,
} from 'app/frontend/base/components/ProtectedContent'
import { Badge } from '@/components/ui/badge'

const TeamSwitcher = () => {
  const { t } = useTranslation()
  const { guild, currentTeam, teamReference } = useGuild()
  const [creatingTeam, setCreatingTeam] = useState(false)
  const navigate = useNavigate()

  const handleTeamSelect = (team: wowaudit.TeamReference) => {
    const currentPath = window.location.pathname.replace(
      currentTeam?.path ?? '',
      '',
    )

    navigate(`${team.path}${currentTeam ? currentPath : ''}`)
  }

  const teamsWithAccess = (guild?.teamReferences || [])
    .filter(
      (team) => hasAccessToContent('member', 'base', guild, team).hasAccess,
    )
    .sort((a, b) => a.orderInGuild - b.orderInGuild)

  return (
    <div className={teamsWithAccess.length || currentTeam ? '' : 'invisible'}>
      {guild && !guild?.teamReferences?.length ? (
        <Button size='sm' onClick={() => setCreatingTeam(true)}>
          {t('dashboard.team.create-first-team')}
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={!teamsWithAccess.length}>
            <span
              className={`inline-flex ${
                !teamsWithAccess.length ? 'cursor-default' : 'cursor-pointer'
              } text-sm items-center font-bold`}
            >
              {teamReference ? (
                <span className='whitespace-nowrap'>
                  {t('shared.team')} {teamReference.name}
                </span>
              ) : (
                <span className='opacity-50'>
                  {t('dashboard.team.choose-team')}
                </span>
              )}
              {teamsWithAccess.length ? (
                <ChevronsUpDown className='ml-2' width={14} />
              ) : null}
            </span>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className='min-w-[--radix-dropdown-menu-trigger-width]'
            align='start'
          >
            {teamsWithAccess.map((team) => (
              <DropdownMenuItem
                key={team.id}
                onSelect={() => handleTeamSelect(team)}
              >
                <div
                  className={`flex w-full items-center gap-4 justify-between ${
                    team.id === currentTeam?.id ? 'text-primary' : ''
                  }`}
                >
                  {team.name}
                  <Badge variant='outline' className='flex items-center gap-1'>
                    <span>{team.characterAmount}</span>
                    <User size={12} />
                  </Badge>
                </div>
              </DropdownMenuItem>
            ))}

            <ProtectedContent
              section='base'
              requirement='admin'
              renderUnauthorized={false}
              requireAllTeams={true}
            >
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className='flex gap-1 items-center'
                onClick={() => setCreatingTeam(true)}
              >
                <Plus width={14} />
                {t('dashboard.team.create-new')}
              </DropdownMenuItem>
            </ProtectedContent>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {guild ? (
        <NewTeamModal setOpen={setCreatingTeam} open={creatingTeam} />
      ) : null}
    </div>
  )
}

export default TeamSwitcher
